import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FileText, Loader2 } from "lucide-react";

export const CreateInvoiceModal = ({
  open,
  onOpenChange,
  clients,
  newInvoice,
  setNewInvoice,
  uninvoicedBookings,
  loadingBookings,
  creatingInvoice,
  onCreateInvoice
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Create Invoice
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Client</Label>
            <Select
              value={newInvoice.client_id}
              onValueChange={(value) => setNewInvoice(prev => ({ ...prev, client_id: value }))}
            >
              <SelectTrigger className="bg-secondary/50 border-black/10" data-testid="invoice-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} {client.company_name && `(${client.company_name})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {newInvoice.client_id && (
            <div className="space-y-2">
              <Label>Select Bookings to Invoice</Label>
              {loadingBookings ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading bookings...</span>
                </div>
              ) : uninvoicedBookings.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-black/10 rounded-lg">
                  No uninvoiced bookings found for this client
                </div>
              ) : (
                <div className="border border-black/10 rounded-lg max-h-[250px] overflow-y-auto">
                  <div className="p-2 bg-secondary/30 border-b border-black/10 flex items-center justify-between sticky top-0">
                    <span className="text-sm font-medium">{uninvoicedBookings.length} bookings available</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (newInvoice.booking_ids.length === uninvoicedBookings.length) {
                          setNewInvoice(prev => ({ ...prev, booking_ids: [] }));
                        } else {
                          setNewInvoice(prev => ({ ...prev, booking_ids: uninvoicedBookings.map(b => b.id) }));
                        }
                      }}
                    >
                      {newInvoice.booking_ids.length === uninvoicedBookings.length ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                  {uninvoicedBookings.map(booking => (
                    <div
                      key={booking.id}
                      className={`p-3 border-b border-white/5 cursor-pointer transition-colors ${
                        newInvoice.booking_ids.includes(booking.id) 
                          ? 'bg-primary/10 border-l-2 border-l-primary' 
                          : 'hover:bg-secondary/30'
                      }`}
                      onClick={() => {
                        setNewInvoice(prev => ({
                          ...prev,
                          booking_ids: prev.booking_ids.includes(booking.id)
                            ? prev.booking_ids.filter(id => id !== booking.id)
                            : [...prev.booking_ids, booking.id]
                        }));
                      }}
                      data-testid={`booking-select-${booking.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={newInvoice.booking_ids.includes(booking.id)}
                            onChange={() => {}}
                            className="h-4 w-4 rounded border-white/20"
                          />
                          <div>
                            <p className="font-mono text-sm text-primary">{booking.booking_ref}</p>
                            <p className="text-sm">{booking.passenger_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">${booking.price?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(booking.pickup_datetime).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {booking.pickup_location} &rarr; {booking.dropoff_location}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {newInvoice.booking_ids.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-primary/10 rounded-lg">
                  <span className="text-sm">{newInvoice.booking_ids.length} booking(s) selected</span>
                  <span className="font-semibold text-primary">
                    Total: ${uninvoicedBookings
                      .filter(b => newInvoice.booking_ids.includes(b.id))
                      .reduce((sum, b) => sum + (b.price || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={newInvoice.due_date}
              onChange={(e) => setNewInvoice(prev => ({ ...prev, due_date: e.target.value }))}
              className="bg-secondary/50 border-black/10"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Billing Period From</Label>
              <Input
                type="date"
                value={newInvoice.billing_period_from}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, billing_period_from: e.target.value }))}
                className="bg-secondary/50 border-black/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Billing Period To</Label>
              <Input
                type="date"
                value={newInvoice.billing_period_to}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, billing_period_to: e.target.value }))}
                className="bg-secondary/50 border-black/10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select
              value={newInvoice.payment_method}
              onValueChange={(value) => setNewInvoice(prev => ({ ...prev, payment_method: value }))}
            >
              <SelectTrigger className="bg-secondary/50 border-black/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer (No Surcharge)</SelectItem>
                <SelectItem value="card">Card Payment (+2.2% Surcharge)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {newInvoice.payment_method === "card" 
                ? "A 2.2% card processing fee will be added to the invoice total"
                : "No surcharge for direct bank transfers"}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input
              placeholder="Invoice notes..."
              value={newInvoice.notes}
              onChange={(e) => setNewInvoice(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-secondary/50 border-black/10"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onCreateInvoice} 
            disabled={creatingInvoice || newInvoice.booking_ids.length === 0}
            data-testid="confirm-create-invoice"
          >
            {creatingInvoice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Invoice ({newInvoice.booking_ids.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
